import { DeviceTypeConfigurations } from "verdi-common";
import { dayMs, minuteMs } from "verdi-common/timeConstants";
import { HashedDaySchedule, Zone } from "verdi-web-client/Models";
import { IrrigationEventProto } from "verdi-web-client/Models/Scheduling/IrrigationEventProto";

import type MapEntityBase from "../../../map-management/map-entities/map-entity-base";
import { getConnectedZones, getDeviceType } from "../general";

/**
 * Irrigating is true if any valve is open
 * returns boolean or null if uncertain
 */
export function isSomeValveOpen({ model }: { model: MapEntityBase["model"] }) {
    const type = getDeviceType({ model });
    const zones = getConnectedZones({ model });
    /**
     * Some sensors have their model set to have valves and record valve state in valveState for verification purposes, but
     * are not actually connected to valves in reality.
     *
     * In this case, check config valveCount since it accurately reflects the true state of the device,
     * instead of valveState
     *
     * When valveCount is undefined or 0 the device has no valves and cannot irrigate, so return false
     */
    if (DeviceTypeConfigurations[type]?.isVerdiDevice !== true || DeviceTypeConfigurations[type]?.valveCount === 0) {
        return false;
    }

    // Model.valvestate is an array [valve1, valve2] or undefined
    const valveStates = model?.valveState?.open ? [...model.valveState.open] : undefined;
    if (valveStates !== undefined) {
        // if it's a single valve or sensor only device, return the valve state
        if (DeviceTypeConfigurations[type]?.valveCount < 2) {
            if (valveStates[0] === valveStates[1]) {
                return valveStates[0];
            }
            return null;
        }
        if (zones) {
            if (zones.length === 1) {
                const zone = zones[0];
                const valve = model.connectedZoneIDs.indexOf(zone.id);
                if (valve !== -1) {
                    return valveStates[valve];
                }
            }
        }
    }
    return null;
}

/**
 * Returns a percentage 0-100 of irrigation completed
 */
export function getPercentageOfIrrigationCompleted({ closestEvent }: { closestEvent: IrrigationEventProto }) {
    const irrigationStartTime = closestEvent ? closestEvent.startTime : 0; // new Date(Date.now() - 1000 * 60 * 30);
    const irrigationEndTime = closestEvent ? closestEvent.endTime : 0; // ;

    let percentDone =
        (Date.now() - irrigationStartTime.valueOf()) /
        Math.max(2, irrigationEndTime.valueOf() - irrigationStartTime.valueOf());
    if (percentDone > 1) {
        percentDone = 1;
    }
    if (percentDone < 0) {
        percentDone = 0;
    }
    return percentDone * 100;
}

interface ClosestIrrigationEventDetails {
    shouldBeIrrigating: boolean;
    closestEvent: IrrigationEventProto | undefined;
    zoneIrrigationStateUncertain: boolean;
}

/**
 * Returns the details of the device based on recent irrigation events, including the closest irrigation event
 *
 * Given multiple events, the closest event is the latest ending event, preferring active events
 */
export function getClosestIrrigationEventDetails({ model }: { model: MapEntityBase["model"] }) {
    const newState: ClosestIrrigationEventDetails = {
        shouldBeIrrigating: false,
        closestEvent: undefined,
        zoneIrrigationStateUncertain: false,
    };
    const zones = getConnectedZones({ model });

    const allActiveEvents: IrrigationEventProto[] = [];
    const allNearbyEvents: IrrigationEventProto[] = [];
    if (Array.isArray(zones) && zones.length > 0) {
        let atLeastOneZoneUncertain = false;
        zones.forEach((z: Zone) => {
            const { activeEvents, nearbyEvents } = getRelevantIrrigationEvents({
                zone: z,
            });
            allActiveEvents.push(...activeEvents);
            allNearbyEvents.push(...nearbyEvents);

            // Uncertain indicates there is an event scheduled but there may not be enough advanced notice to irrigate
            // @ts-ignore zoneIrrigationScheduled exists but not properly typed
            const zoneUncertain = z.recentData?.zoneIrrigationScheduled?.value === 0.5;
            const zoneShouldBeIrrigating = activeEvents.length > 0;

            // Bubble up the should be irrigating state. If any zone should be irrigating, the device should be irrigating
            newState.shouldBeIrrigating = newState.shouldBeIrrigating || zoneShouldBeIrrigating;

            // Bubble up the uncertainty state
            atLeastOneZoneUncertain = atLeastOneZoneUncertain || zoneUncertain;
        });

        // If any zone is uncertain and the device is not currently irrigating, the device is uncertain
        newState.zoneIrrigationStateUncertain = atLeastOneZoneUncertain && !newState.shouldBeIrrigating;
    }

    // Sort all events by end time (latest to earliest), then pick the latest ending event, preferring active events
    const sortedActiveEvents = allActiveEvents.sort((a, b) => b.endTime.valueOf() - a.endTime.valueOf());
    const sortedNearbyEvents = allNearbyEvents.sort((a, b) => b.endTime.valueOf() - a.endTime.valueOf());
    const sortedEvents = [...sortedActiveEvents, ...sortedNearbyEvents];

    newState.closestEvent = sortedEvents[0] || undefined;

    return newState;
}

const NEARBY_IRRIGATION_EVENT_THRESHOLD = minuteMs * 30;

interface GetCurrentIrrigationInfoForZoneParams {
    zone: Zone;
}

/**
 * Returns all active and recent events for a zone
 *
 * Active Event: An event that has already started and is not yet finished.
 * Nearby Event: An event that will start in the next N minutes or ended in the last N minutes
 */
function getRelevantIrrigationEvents({ zone }: GetCurrentIrrigationInfoForZoneParams) {
    // Get all data for yesterday, today, and tomorrow. We need the buffers since we may be at midnight on a day
    // and need to check the previous or next day.
    const relevantDays = [
        HashedDaySchedule.getHashRecord(new Date(Date.now()), zone.id),
        HashedDaySchedule.getHashRecord(new Date(Date.now() + dayMs), zone.id),
        HashedDaySchedule.getHashRecord(new Date(Date.now() - dayMs), zone.id),
    ];

    const activeEvents: IrrigationEventProto[] = [];
    const nearbyEvents: IrrigationEventProto[] = [];

    // For each day, get all active and close events happening for that day
    relevantDays.forEach((rd) => {
        rd.includedEvents.forEach((e: IrrigationEventProto) => {
            if (e === undefined) {
                return;
            }

            // If the event has started and is not over, it is an active event
            if (e.startTime.valueOf() < Date.now() && e.endTime.valueOf() > Date.now()) {
                activeEvents.push(e);
                return;
            }

            // If the event will start in the next N minutes or has ended in the last N minutes, it is a recent event
            const now = Date.now();
            const startsSoon =
                e.startTime.valueOf() >= now && e.startTime.valueOf() <= now + NEARBY_IRRIGATION_EVENT_THRESHOLD;
            const endedRecently =
                e.endTime.valueOf() <= now && e.endTime.valueOf() >= now - NEARBY_IRRIGATION_EVENT_THRESHOLD;
            if (startsSoon || endedRecently) {
                nearbyEvents.push(e);
            }
        });
    });

    return {
        activeEvents,
        nearbyEvents,
    };
}
